import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import adminApi from '../api/adminApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EXCLUDED_CREATE_ROLES = new Set(['adminleveluser', 'topleveluser']);

const toTitleCase = (value) =>
  String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const guessNameFromEmail = (email) => {
  const raw = String(email || '').trim();
  const at = raw.indexOf('@');
  const local = (at >= 0 ? raw.slice(0, at) : raw).trim();
  const withoutPlus = local.split('+')[0];

  const tokens = withoutPlus
    .replace(/[^a-zA-Z0-9._\-\s]/g, ' ')
    .split(/[._\-\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  if (tokens.length === 0) return { name: '', surname: '' };

  const [first, ...rest] = tokens;
  const name = toTitleCase(first);
  const surname = rest.length ? toTitleCase(rest.join(' ')) : '';
  return { name, surname };
};

const getCrypto = () => {
  try {
    return window.crypto || window.msCrypto;
  } catch (_) {
    return null;
  }
};

const randomInt = (maxExclusive) => {
  const max = Number(maxExclusive);
  if (!Number.isFinite(max) || max <= 0) return 0;

  const cryptoObj = getCrypto();
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const array = new Uint32Array(1);
    cryptoObj.getRandomValues(array);
    return array[0] % max;
  }

  return Math.floor(Math.random() * max);
};

const shuffleInPlace = (arr) => {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
};

const generateTemporaryPassword = (length = 14) => {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const special = '!@#$%^&*_-+';
  const all = lower + upper + digits + special;

  const targetLength = Math.max(10, Number(length) || 14);
  const chars = [
    lower[randomInt(lower.length)],
    upper[randomInt(upper.length)],
    digits[randomInt(digits.length)],
    special[randomInt(special.length)],
  ];

  while (chars.length < targetLength) {
    chars.push(all[randomInt(all.length)]);
  }

  shuffleInPlace(chars);
  return chars.join('');
};

const AdminCreateUser = () => {
  const navigate = useNavigate();
  const msgs = useRef(null);
  const toast = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [roles, setRoles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [appProviders, setAppProviders] = useState([]);

  const [suggestedFromEmail, setSuggestedFromEmail] = useState({ name: '', surname: '' });
  const [nameAuto, setNameAuto] = useState(false);
  const [surnameAuto, setSurnameAuto] = useState(false);
	const [passwordAuto, setPasswordAuto] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    role: '',
    customerId: null,
    locationId: null,
    appProviderId: null,
    sendWelcomeEmail: true,
  });

  const isCustomerRole = form.role === 'CustomerLevelUser';
  const isLocationRole = form.role === 'LocationLevelUser';
  const isAppProviderRole = form.role === 'AppProviderUser' || form.role === 'AppProviderLevelUser';

  const roleOptions = roles
    .filter((r) => !EXCLUDED_CREATE_ROLES.has(String(r || '').trim().toLowerCase()))
    .map((r) => ({ label: r, value: r }));
  const customerOptions = customers.map((c) => ({ label: c.name, value: c.id }));
  const appProviderOptions = appProviders.map((a) => ({ label: a.name, value: a.id }));

  const onEmailChange = (email) => {
    const nextSuggested = guessNameFromEmail(email);
    setSuggestedFromEmail(nextSuggested);

    const shouldGeneratePassword =
      (!String(form.password || '').trim() || passwordAuto) &&
      !String(form.email || '').trim() &&
      String(email || '').trim();

    const nextGeneratedPassword = shouldGeneratePassword ? generateTemporaryPassword() : '';
    if (nextGeneratedPassword) {
      setPasswordAuto(true);
    }

    setForm((prev) => {
      const next = { ...prev, email };

      const shouldAutofillName = !String(prev.name || '').trim() || nameAuto;
      const shouldAutofillSurname = !String(prev.surname || '').trim() || surnameAuto;

      if (shouldAutofillName) {
        next.name = nextSuggested.name;
      }

      if (shouldAutofillSurname) {
        next.surname = nextSuggested.surname;
      }

			if (nextGeneratedPassword) {
				next.password = nextGeneratedPassword;
			}

      return next;
    });

    setNameAuto(Boolean(nextSuggested.name));
    setSurnameAuto(Boolean(nextSuggested.surname));
  };

  const handleError = (error, fallbackMessage) => {
    if (handleAccessError(error)) {
      return;
    }

    const detail =
      (error && error.response && error.response.data) ||
      (error && error.message) ||
      fallbackMessage;

    if (msgs.current) {
      msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    }
  };

  const loadInitial = async () => {
    try {
      setLoading(true);
      clearAccessIssue();
      if (msgs.current) msgs.current.clear();

      const [rolesResp, customersResp, appProvidersResp] = await Promise.all([
        adminApi.getRoles(),
        adminApi.getCustomersLookup(),
        adminApi.getAppProvidersLookup(),
      ]);

      setRoles(Array.isArray(rolesResp) ? rolesResp : []);
      setCustomers(Array.isArray(customersResp) ? customersResp : []);
      setAppProviders(Array.isArray(appProvidersResp) ? appProvidersResp : []);
    } catch (error) {
      handleError(error, 'Failed to load create-user metadata.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    const refreshCatalog = () => loadInitial();
    window.addEventListener('smarttile:catalog-updated', refreshCatalog);
    return () => window.removeEventListener('smarttile:catalog-updated', refreshCatalog);
  }, []);

  const onRoleChange = (role) => {
    setForm((prev) => ({
      ...prev,
      role,
      customerId: null,
      locationId: null,
      appProviderId: null,
    }));
    setLocations([]);
  };

  const onLocationCustomerChange = async (customerId) => {
    setForm((prev) => ({ ...prev, customerId, locationId: null }));
    setLocations([]);

    if (customerId === null || customerId === undefined) return;

    try {
      setLocationsLoading(true);
      const nextLocations = await adminApi.getLocationsByCustomerLookup(customerId);
      setLocations(Array.isArray(nextLocations) ? nextLocations : []);
    } catch (error) {
      handleError(error, 'Failed to load locations for this customer.');
    } finally {
      setLocationsLoading(false);
    }
  };

  const validate = () => {
    if (!form.email.trim() || !EMAIL_PATTERN.test(form.email.trim())) {
      return 'A valid email is required.';
    }

    if (!String(form.password || '').trim()) {
      return 'Password is required.';
    }

    if (!form.name.trim()) return 'Name is required.';
    if (!form.surname.trim()) return 'Surname is required.';
    if (!form.role) return 'Role is required.';

    if (isCustomerRole && (form.customerId === null || form.customerId === undefined)) {
      return 'Customer is required for CustomerLevelUser.';
    }

    if (isLocationRole && (form.customerId === null || form.customerId === undefined)) {
      return 'Customer is required for LocationLevelUser.';
    }

    if (isLocationRole && (form.locationId === null || form.locationId === undefined)) {
      return 'Location is required for LocationLevelUser.';
    }

    if (isAppProviderRole && (form.appProviderId === null || form.appProviderId === undefined)) {
      return 'App Provider is required for AppProvider role.';
    }

    return null;
  };

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const extractCreatedUserId = (createdUserResponse) => {
    if (!createdUserResponse || typeof createdUserResponse !== 'object') return null;

    const candidates = [
      createdUserResponse.id,
      createdUserResponse.userId,
      createdUserResponse.user && createdUserResponse.user.id,
      createdUserResponse.user && createdUserResponse.user.userId,
      createdUserResponse.data && createdUserResponse.data.id,
      createdUserResponse.data && createdUserResponse.data.userId,
    ];

    for (const candidate of candidates) {
      const asNumber = Number(candidate);
      if (Number.isFinite(asNumber)) return asNumber;
    }

    return null;
  };

  const findUserIdByEmail = async (email) => {
    const target = normalizeEmail(email);
    if (!target) return null;

    const users = await adminApi.getUsersSummary();
    const match = Array.isArray(users)
      ? users.find((u) => normalizeEmail(u && u.email) === target)
      : null;

    const asNumber = Number(match && match.id);
    return Number.isFinite(asNumber) ? asNumber : null;
  };

  const enableDefaultUserSettings = async (userId) => {
    const id = Number(userId);
    if (!Number.isFinite(id)) return { ok: false, errors: ['Missing user id'] };

    const results = await Promise.allSettled([
      adminApi.setTileTimeLimitationFeatureFlag(id, true),
      adminApi.setUserFeatureFlag(id, 'notification-settings', true),
    ]);

    const errors = results
      .map((result) => (result.status === 'rejected' ? result.reason : null))
      .filter(Boolean)
      .map((err) => (err && err.response && err.response.data) || (err && err.message) || String(err));

    return { ok: errors.length === 0, errors };
  };

  const createUser = async () => {
    const validationError = validate();
    if (validationError) {
      if (msgs.current) {
        msgs.current.show({ severity: 'warn', summary: '', detail: validationError });
      }
      return;
    }

    const payload = {
      email: form.email.trim(),
      password: form.password,
      name: form.name.trim(),
      surname: form.surname.trim(),
      role: form.role,
      customerId: isCustomerRole || isLocationRole ? Number(form.customerId) : null,
      locationId: isLocationRole ? Number(form.locationId) : null,
      appProviderId: isAppProviderRole ? Number(form.appProviderId) : null,
      callbackUrl: window.location.origin,
      sendWelcomeEmail: Boolean(form.sendWelcomeEmail),
    };

    try {
      setSaving(true);
      const created = await adminApi.createUser(payload);

      // Enable defaults for all new users.
      let createdUserId = extractCreatedUserId(created);
      if (!createdUserId) {
        // Fallback: users list can be eventually consistent, so retry a couple of times.
        for (let attempt = 0; attempt < 3 && !createdUserId; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 250));
          // eslint-disable-next-line no-await-in-loop
          createdUserId = await findUserIdByEmail(payload.email);
        }
      }

      const defaultEnable = await enableDefaultUserSettings(createdUserId);

      if (toast.current) {
        toast.current.show({
          severity: defaultEnable.ok ? 'success' : 'warn',
          summary: '',
          detail: payload.sendWelcomeEmail
            ? defaultEnable.ok
              ? 'User created; welcome email sent.'
              : 'User created; welcome email sent (but defaults could not be enabled — enable manually in Admin → Users).'
            : defaultEnable.ok
              ? 'User created; welcome email not sent.'
              : 'User created; welcome email not sent (but defaults could not be enabled — enable manually in Admin → Users).',
          life: 1200,
        });
      }

      setTimeout(() => {
        navigate('/admin/users', {
          replace: true,
          state: {
            refreshAt: Date.now(),
            flashSuccess: payload.sendWelcomeEmail
              ? defaultEnable.ok
                ? 'User created; welcome email sent.'
                : 'User created; welcome email sent (defaults not enabled; check user flags).'
              : defaultEnable.ok
                ? 'User created; welcome email not sent.'
                : 'User created; welcome email not sent (defaults not enabled; check user flags).',
          },
        });
      }, 250);
    } catch (error) {
      handleError(error, 'Failed to create user.');
    } finally {
      setSaving(false);
    }
  };

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate('/admin/users')} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3">Admin - Create User</h4>
          </div>
        </div>

        <Messages ref={msgs} />
        <Toast ref={toast} />

        {(loading || saving) && progressbar}

        <div className="p-fluid" style={{ maxWidth: '700px' }}>
          <div className="field mb-3">
            <label htmlFor="create-email">Email</label>
            <InputText
              id="create-email"
              value={form.email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="new@domain.com"
            />
            {(suggestedFromEmail.name || suggestedFromEmail.surname) && (
              <small className="text-600">
                Suggested from email: {suggestedFromEmail.name}{suggestedFromEmail.surname ? ` ${suggestedFromEmail.surname}` : ''}
              </small>
            )}
          </div>

          <div className="field mb-3">
            <label htmlFor="create-name">Name</label>
            <InputText
              id="create-name"
              value={form.name}
              onChange={(e) => {
                setNameAuto(false);
                setForm((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="create-surname">Surname</label>
            <InputText
              id="create-surname"
              value={form.surname}
              onChange={(e) => {
                setSurnameAuto(false);
                setForm((prev) => ({ ...prev, surname: e.target.value }));
              }}
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="create-password">Password</label>
            <InputText
              id="create-password"
              type="password"
              value={form.password}
              onChange={(e) => {
						setPasswordAuto(false);
						setForm((prev) => ({ ...prev, password: e.target.value }));
					}}
              placeholder="Enter temporary password"
            />
				{passwordAuto && String(form.password || '').trim() && (
					<small className="text-600">Temporary password auto-generated (you can edit it).</small>
				)}
          </div>

          <div className="field mb-3">
            <label htmlFor="create-role">Role</label>
            <Dropdown
              id="create-role"
              value={form.role}
              options={roleOptions}
              onChange={(e) => onRoleChange(e.value)}
              placeholder="Select role"
              style={{ minWidth: '280px' }}
            />
          </div>

          <div className="field mb-3">
            <label htmlFor="create-send-welcome-email">Send welcome email</label>
            <div className="flex align-items-center" style={{ gap: '0.75rem' }}>
              <InputSwitch
                id="create-send-welcome-email"
                checked={Boolean(form.sendWelcomeEmail)}
                onChange={(e) => setForm((prev) => ({ ...prev, sendWelcomeEmail: e.value }))}
              />
              <small className="text-600">
                If enabled, the user will receive the welcome email with the link to set a password.
              </small>
            </div>
          </div>

          {(isCustomerRole || isLocationRole) && (
            <div className="field mb-3">
              <label htmlFor="create-customer">Customer</label>
              <Dropdown
                id="create-customer"
                value={form.customerId}
                options={customerOptions}
                onChange={(e) => isLocationRole ? onLocationCustomerChange(e.value) : setForm((prev) => ({ ...prev, customerId: e.value }))}
                placeholder="Select customer"
                filter
                style={{ minWidth: '320px' }}
              />
            </div>
          )}

          {isLocationRole && (
            <div className="field mb-3">
              <label htmlFor="create-location">Location</label>
              <Dropdown
                id="create-location"
                value={form.locationId}
                options={locations.map((location) => ({ label: location.name, value: location.id }))}
                onChange={(e) => setForm((prev) => ({ ...prev, locationId: e.value }))}
                placeholder={locationsLoading ? 'Loading locations...' : 'Select location'}
                disabled={form.customerId === null || form.customerId === undefined || locationsLoading}
                filter
                style={{ minWidth: '320px' }}
              />
            </div>
          )}

          {isAppProviderRole && (
            <div className="field mb-3">
              <label htmlFor="create-appprovider">App Provider</label>
              <Dropdown
                id="create-appprovider"
                value={form.appProviderId}
                options={appProviderOptions}
                onChange={(e) => setForm((prev) => ({ ...prev, appProviderId: e.value }))}
                placeholder="Select app provider"
                filter
                style={{ minWidth: '320px' }}
              />
            </div>
          )}

          <div className="flex align-items-center gap-2 mt-4">
            <Button label="Create User" icon="pi pi-user-plus" onClick={createUser} disabled={loading || saving} />
            <Button label="Cancel" className="p-button-text" onClick={() => navigate('/admin/users')} disabled={saving} />
          </div>
        </div>
      </Container>
    </Card>
  );
};

export default AdminCreateUser;
